import { apiClient } from './apiClient';
import { authFetch } from './authService';
import {
  GroundTruthDocument,
  GroundTruthFieldEditRequest,
  GroundTruthFlagsUpdateRequest,
  PdfUploadResponse,
  ExcelPreviewResponse,
  ExcelConfirmResponse,
  FieldEditResponse,
  FlagsUpdateResponse,
  GroundTruthDeleteResponse,
  GtBenchmarkRunOption,
  GtBenchmarkResultDetail,
  FieldDeleteResponse,
  FieldCountResponse,
  FieldBulkDeleteResponse,
} from '../types';

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8001'}/api/v1`;

export const groundTruthService = {
  async list(
    domain?: string,
    tag?: string,
    options?: { excludeTags?: string[] }
  ): Promise<GroundTruthDocument[]> {
    const params = new URLSearchParams();
    if (domain) params.set('domain', domain);
    if (tag) params.set('tag', tag);
    if (options?.excludeTags && options.excludeTags.length > 0) {
      params.set('exclude_tags', options.excludeTags.join(','));
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<GroundTruthDocument[]>(`/admin/ground-truth${qs}`);
    return response.data;
  },

  async get(id: string): Promise<GroundTruthDocument> {
    const response = await apiClient.get<GroundTruthDocument>(`/admin/ground-truth/${id}`);
    return response.data;
  },

  async uploadPdfs(files: File[]): Promise<PdfUploadResponse> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const response = await authFetch(`${API_BASE}/admin/ground-truth/upload-pdf`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
      throw new Error(error.detail || 'Erreur lors de l\'upload des PDFs');
    }

    return response.json();
  },

  async importExcelPreview(file: File, domain?: string): Promise<ExcelPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (domain) formData.append('domain', domain);

    const response = await authFetch(`${API_BASE}/admin/ground-truth/import-excel`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
      throw new Error(error.detail || 'Erreur lors de l\'analyse du fichier Excel');
    }

    return response.json();
  },

  async importExcelConfirm(
    previewToken: string,
    domain?: string,
    duplicateMode: string = 'skip',
    tags: string[] = [],
    clearEmptyCells: boolean = false,
  ): Promise<ExcelConfirmResponse> {
    const response = await apiClient.post<ExcelConfirmResponse>(
      '/admin/ground-truth/import-excel/confirm',
      {
        preview_token: previewToken,
        domain,
        duplicate_mode: duplicateMode,
        tags,
        clear_empty_cells: clearEmptyCells,
      },
    );
    return response.data;
  },

  async editField(documentId: number, data: GroundTruthFieldEditRequest): Promise<FieldEditResponse> {
    const response = await apiClient.patch<FieldEditResponse>(
      `/admin/ground-truth/${documentId}/field`,
      data
    );
    return response.data;
  },

  async updateFlags(documentId: number, data: GroundTruthFlagsUpdateRequest): Promise<FlagsUpdateResponse> {
    const response = await apiClient.patch<FlagsUpdateResponse>(
      `/admin/ground-truth/${documentId}/flags`,
      data
    );
    return response.data;
  },

  async deleteDocument(documentId: number): Promise<GroundTruthDeleteResponse> {
    const response = await authFetch(`${API_BASE}/admin/ground-truth/${documentId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
      throw new Error(error.detail || 'Erreur lors de la suppression');
    }

    return response.json();
  },

  // PROD1.7.5 — Field-level management
  async deleteField(documentId: number, fieldKey: string): Promise<FieldDeleteResponse> {
    const url = `${API_BASE}/admin/ground-truth/${documentId}/fields?field_key=${encodeURIComponent(fieldKey)}`;
    const response = await authFetch(url, { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
      throw new Error(error.detail || 'Erreur lors de la suppression du champ');
    }
    return response.json();
  },

  async countFieldOccurrences(fieldKey: string): Promise<FieldCountResponse> {
    const response = await apiClient.get<FieldCountResponse>(
      `/admin/ground-truth/fields/count?field_key=${encodeURIComponent(fieldKey)}`
    );
    return response.data;
  },

  async bulkDeleteField(fieldKey: string, expectedCount: number): Promise<FieldBulkDeleteResponse> {
    const response = await apiClient.post<FieldBulkDeleteResponse>(
      '/admin/ground-truth/fields/bulk-delete',
      { field_key: fieldKey, expected_count: expectedCount }
    );
    return response.data;
  },

  // PROD1.7.5 — Download xlsx export for the user's current selection.
  // POST (not GET) because the selection list lives in the body — too long
  // for a query string when the user picks dozens of docs.
  async exportExcel(documentIds: number[]): Promise<void> {
    const response = await authFetch(`${API_BASE}/admin/ground-truth/export-excel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_ids: documentIds }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
      throw new Error(error.detail || "Erreur lors de l'export Excel");
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : 'gt_export.xlsx';

    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = filename;
    window.document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  async updateTags(documentId: number, tags: string[]): Promise<{ id: number; tags: string[] }> {
    const response = await apiClient.patch<{ id: number; tags: string[] }>(
      `/admin/ground-truth/${documentId}/tags`,
      { tags }
    );
    return response.data;
  },

  async listDistinctTags(domain?: string): Promise<string[]> {
    const params = domain ? `?domain=${domain}` : '';
    const response = await apiClient.get<string[]>(`/admin/ground-truth/tags/distinct${params}`);
    return response.data;
  },

  getDownloadUrl(documentId: number): string {
    return `${API_BASE}/admin/ground-truth/${documentId}/pdf`;
  },

  async listBenchmarkResults(documentId: string): Promise<GtBenchmarkRunOption[]> {
    const response = await apiClient.get<GtBenchmarkRunOption[]>(
      `/admin/ground-truth/${documentId}/benchmark-results`
    );
    return response.data;
  },

  async getBenchmarkResult(documentId: string, runId: string): Promise<GtBenchmarkResultDetail> {
    const response = await apiClient.get<GtBenchmarkResultDetail>(
      `/admin/ground-truth/${documentId}/benchmark-results/${runId}`
    );
    return response.data;
  },

  async getPdfUrl(documentId: number): Promise<string> {
    const response = await authFetch(`${API_BASE}/admin/ground-truth/${documentId}/pdf`);

    if (!response.ok) {
      throw new Error('Failed to fetch ground truth PDF');
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
};
